import { useState } from 'react';
import { exportService } from '@services/exportService';
import toast from 'react-hot-toast';
import {
  TrendingUp,
  TrendingDown,
  BarChart3,
  Wallet,
  ArrowUpRight,
  ArrowDownRight,
  Download,
} from 'lucide-react';
import {
  useBalanceEvolution,
  useMonthlyComparison,
  useCategoryReport,
  useCashFlowProjection,
  useSummaryReport,
} from '@hooks/useReports';
import PageHeader from '@components/layout/PageHeader';
import Card from '@components/common/Card';
import Button from '@components/common/Button';
import Select from '@components/common/Select';
import Loading from '@components/common/Loading';
import Badge from '@components/common/Badge';
import PageTutorial from '@components/common/PageTutorial';
import {
  BalanceEvolutionChart,
  MonthlyComparisonChart,
  CategoryPieChart,
  CashFlowChart,
} from '@components/charts';
import { formatCurrency } from '@utils/formatters';
import { clsx } from 'clsx';

// ─── MetricCard ───────────────────────────────────────────────────────────────
const MetricCard = ({ title, value, change, icon, iconBg, valueColor }) => (
  <div className="bg-white dark:bg-gray-800 rounded-2xl border border-gray-200 dark:border-gray-700 shadow-sm p-5">
    <div className="flex items-start justify-between mb-3">
      <p className="text-xs font-medium text-gray-500 dark:text-gray-400">{title}</p>
      <div className={clsx('p-2.5 rounded-xl', iconBg)}>{icon}</div>
    </div>
    <p className={clsx('text-2xl font-bold', valueColor)}>
      {formatCurrency(value)}
    </p>
    {change !== undefined && (
      <div className={clsx(
        'flex items-center gap-1 mt-2 text-xs font-medium',
        change >= 0 ? 'text-emerald-600' : 'text-red-500'
      )}>
        {change >= 0 ? <ArrowUpRight size={13} /> : <ArrowDownRight size={13} />}
        {Math.abs(change).toFixed(1)}% vs mês anterior
      </div>
    )}
  </div>
);

const Reports = () => {
  const [months, setMonths] = useState(6);
  const [categoryPeriod] = useState(() => {
    const now = new Date();
    const start = new Date(now.getFullYear(), now.getMonth(), 1);
    const end = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return {
      startDate: start.toISOString().split('T')[0],
      endDate: end.toISOString().split('T')[0],
    };
  });

  const { data: summaryData, isLoading: summaryLoading } = useSummaryReport();
  const { data: balanceData, isLoading: balanceLoading } = useBalanceEvolution(months);
  const { data: comparisonData, isLoading: comparisonLoading } = useMonthlyComparison(months);
  const { data: categoryData, isLoading: categoryLoading } = useCategoryReport(
    categoryPeriod.startDate,
    categoryPeriod.endDate
  );
  const [projectionMonths, setProjectionMonths] = useState(6);
  const { data: cashFlowData, isLoading: cashFlowLoading } = useCashFlowProjection(projectionMonths);

  const isLoading = summaryLoading || balanceLoading || comparisonLoading || categoryLoading || cashFlowLoading;

  const [isExporting, setIsExporting] = useState(false);
  const handleExport = async () => {
    setIsExporting(true);
    try {
      await exportService.exportCSV();
      toast.success('Exportação concluída! O download iniciou automaticamente.');
    } catch {
      toast.error('Erro ao exportar relatório. Tente novamente.');
    } finally {
      setIsExporting(false);
    }
  };

  if (isLoading && !summaryData) {
    return <Loading text="Carregando relatórios..." />;
  }

  const periodOptions = [
    { value: '3', label: 'Últimos 3 meses' },
    { value: '6', label: 'Últimos 6 meses' },
    { value: '12', label: 'Últimos 12 meses' },
  ];

  return (
    <div className="space-y-6 pb-8">
      <PageHeader
        title="Relatórios"
        subtitle="Análise detalhada das suas finanças"
        action={
          <div className="flex items-center gap-3">
            <Select
              options={periodOptions}
              value={months.toString()}
              onChange={(e) => setMonths(parseInt(e.target.value))}
              fullWidth={false}
              containerClassName="w-48"
            />
            <Button
              variant="secondary"
              leftIcon={<Download size={18} />}
              onClick={handleExport}
              isLoading={isExporting}
            >
              Exportar
            </Button>
          </div>
        }
      />

      <PageTutorial
        pageId="reports"
        title="Relatorios financeiros"
        steps={[
          'Analise seus gastos por categoria com graficos interativos',
          'Compare meses diferentes para identificar tendencias',
          'Exporte relatorios em CSV ou JSON para uso externo',
        ]}
      />

      {/* Summary Cards */}
      {summaryData && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <MetricCard
            title="Receitas do Mês"
            value={summaryData.currentMonth.income}
            change={summaryData.variation.income}
            icon={<TrendingUp size={18} className="text-emerald-600" />}
            iconBg="bg-emerald-100 dark:bg-emerald-900/30"
            valueColor="text-emerald-600"
          />
          <MetricCard
            title="Despesas do Mês"
            value={summaryData.currentMonth.expense}
            change={summaryData.variation.expense != null ? -summaryData.variation.expense : undefined}
            icon={<TrendingDown size={18} className="text-red-600" />}
            iconBg="bg-red-100 dark:bg-red-900/30"
            valueColor="text-red-600"
          />
          <MetricCard
            title="Saldo do Mês"
            value={summaryData.currentMonth.balance}
            icon={
              summaryData.currentMonth.balance >= 0
                ? <BarChart3 size={18} className="text-primary-600" />
                : <BarChart3 size={18} className="text-red-600" />
            }
            iconBg={
              summaryData.currentMonth.balance >= 0
                ? 'bg-primary-100 dark:bg-primary-900/30'
                : 'bg-red-100 dark:bg-red-900/30'
            }
            valueColor={summaryData.currentMonth.balance >= 0 ? 'text-primary-600' : 'text-red-600'}
          />
          <MetricCard
            title="Patrimônio Total"
            value={summaryData.accounts.totalBalance}
            icon={<Wallet size={18} className={summaryData.accounts.totalBalance >= 0 ? 'text-primary-600' : 'text-red-600'} />}
            iconBg={summaryData.accounts.totalBalance >= 0 ? 'bg-primary-100 dark:bg-primary-900/30' : 'bg-red-100 dark:bg-red-900/30'}
            valueColor={summaryData.accounts.totalBalance >= 0 ? 'text-gray-900 dark:text-white' : 'text-red-600'}
          />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card title="Evolução do Saldo" subtitle="Patrimônio ao longo do tempo">
          {balanceLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loading />
            </div>
          ) : (
            <BalanceEvolutionChart data={balanceData} />
          )}
        </Card>

        <Card title="Receitas vs Despesas" subtitle="Comparativo mensal">
          {comparisonLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loading />
            </div>
          ) : (
            <MonthlyComparisonChart data={comparisonData} />
          )}
        </Card>
      </div>

      {/* Charts Row 2 - Categories */}
      <div className="grid lg:grid-cols-2 gap-6">
        <Card
          title="Despesas por Categoria"
          subtitle={`${categoryPeriod.startDate} a ${categoryPeriod.endDate}`}
          headerAction={
            categoryData && (
              <Badge variant="danger">
                Total: {formatCurrency(categoryData.expense.total)}
              </Badge>
            )
          }
        >
          {categoryLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loading />
            </div>
          ) : categoryData?.expense?.categories?.length > 0 ? (
            <CategoryPieChart data={categoryData.expense.categories} type="expense" />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Sem despesas no período
            </div>
          )}
        </Card>

        <Card
          title="Receitas por Categoria"
          subtitle={`${categoryPeriod.startDate} a ${categoryPeriod.endDate}`}
          headerAction={
            categoryData && (
              <Badge variant="success">
                Total: {formatCurrency(categoryData.income.total)}
              </Badge>
            )
          }
        >
          {categoryLoading ? (
            <div className="h-64 flex items-center justify-center">
              <Loading />
            </div>
          ) : categoryData?.income?.categories?.length > 0 ? (
            <CategoryPieChart data={categoryData.income.categories} type="income" />
          ) : (
            <div className="h-64 flex items-center justify-center text-gray-500">
              Sem receitas no período
            </div>
          )}
        </Card>
      </div>

      {/* Cash Flow Projection */}
      <Card
        title="Projeção de Fluxo de Caixa"
        subtitle="Previsão baseada em recorrências e parcelamentos"
        headerAction={
          <div className="flex items-center gap-3">
            {cashFlowData && (
              <Badge variant="primary">
                Saldo Atual: {formatCurrency(cashFlowData.currentBalance)}
              </Badge>
            )}
            <Select
              options={[
                { value: '3', label: '3 meses' },
                { value: '6', label: '6 meses' },
                { value: '12', label: '12 meses' },
                { value: '24', label: '24 meses' },
                { value: '36', label: '36 meses' },
              ]}
              value={projectionMonths.toString()}
              onChange={(e) => setProjectionMonths(parseInt(e.target.value))}
              fullWidth={false}
              containerClassName="w-32"
            />
          </div>
        }
      >
        {cashFlowLoading ? (
          <div className="h-64 flex items-center justify-center">
            <Loading />
          </div>
        ) : cashFlowData?.projection?.length > 0 ? (
          <CashFlowChart data={cashFlowData.projection} />
        ) : (
          <div className="h-64 flex items-center justify-center text-gray-500">
            Adicione recorrências para ver a projeção
          </div>
        )}
      </Card>

      {/* Summary Stats */}
      {summaryData && (
        <div className="grid lg:grid-cols-3 gap-6">
          <Card title="Contas" subtitle="Resumo das contas cadastradas">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Total de Contas</span>
                <span className="font-semibold">{summaryData.accounts.count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Saldo Total</span>
                <span className={clsx(
                  'font-semibold',
                  summaryData.accounts.totalBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
                )}>
                  {formatCurrency(summaryData.accounts.totalBalance)}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Recorrências" subtitle="Receitas e despesas fixas">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Receitas Fixas</span>
                <span className="font-semibold text-emerald-600">
                  {formatCurrency(summaryData.recurrences.monthlyIncome)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Despesas Fixas</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(summaryData.recurrences.monthlyExpense)}
                </span>
              </div>
              <div className="flex justify-between pt-2 border-t border-gray-200 dark:border-gray-700">
                <span className="text-gray-500 dark:text-gray-400">Saldo Mensal</span>
                <span className={clsx(
                  'font-semibold',
                  summaryData.recurrences.monthlyBalance >= 0 ? 'text-emerald-600' : 'text-red-600'
                )}>
                  {formatCurrency(summaryData.recurrences.monthlyBalance)}
                </span>
              </div>
            </div>
          </Card>

          <Card title="Parcelamentos" subtitle="Compras parceladas ativas">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Parcelamentos Ativos</span>
                <span className="font-semibold">{summaryData.installments.count}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-500 dark:text-gray-400">Total Restante</span>
                <span className="font-semibold text-red-600">
                  {formatCurrency(summaryData.installments.totalRemaining)}
                </span>
              </div>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
};

export default Reports;
